import React, { useEffect, useRef, useState } from 'react';
import { Home, Building, Wrench, Car, Shield, PaintBucket } from 'lucide-react';

const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.fade-up, .fade-left, .fade-right, .scale-in').forEach((child) => {
            child.classList.add('visible');
          });
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return ref;
};

const services = [
  {
    icon: Home,
    title: 'Construcción de Vivienda',
    desc: 'Diseño y edificación de residencias y conjuntos habitacionales con los más altos estándares de calidad y acabados modernos.',
    backDesc: 'Desde casas individuales hasta conjuntos residenciales completos. Materiales de primera calidad y acabados que superan expectativas.',
    image: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/ccmk3xor_CURRICULUM%20CASAS%20WEB%20COMPLEMENTO%202023-05.jpg',
  },
  {
    icon: Building,
    title: 'Obra Pública y Privada',
    desc: 'Construcción de infraestructura municipal, estatal y federal, cumpliendo todas las normas y regulaciones vigentes.',
    backDesc: 'Experiencia comprobada con dependencias gubernamentales. Proyectos entregados en tiempo y forma, con transparencia total.',
    image: 'https://customer-assets.emergentagent.com/job_web-preview-116/artifacts/5w3poglw_WhatsApp%20Image%202026-04-06%20at%2012.41.13%20PM.jpeg',
  },
  {
    icon: Wrench,
    title: 'Remodelaciones y Ampliaciones',
    desc: 'Transformamos espacios existentes con soluciones innovadoras que maximizan el aprovechamiento y valor del inmueble.',
    backDesc: 'Evaluación estructural previa, propuesta de diseño, ejecución profesional y entrega con garantía incluida.',
    image: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/lhnnfgql_2_1.png',
  },
  {
    icon: Car,
    title: 'Pavimentación',
    desc: 'Pavimentación de calles, avenidas, estacionamientos y accesos con materiales de alta durabilidad.',
    backDesc: 'Técnicas modernas de pavimentación. Diagnóstico de suelo, capas base y carpeta asfáltica de alta resistencia.',
    image: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/i3wceneu_WhatsApp%20Image%202026-04-02%20at%2011.30.58%20AM.jpeg',
  },
  {
    icon: Shield,
    title: 'Conservación y Mantenimiento',
    desc: 'Programas integrales de mantenimiento preventivo y correctivo para toda clase de infraestructura.',
    backDesc: 'Inspecciones periódicas, reportes detallados y ejecución rápida de mantenimientos para prolongar la vida útil de su infraestructura.',
    image: 'https://customer-assets.emergentagent.com/job_yescas-evolved/artifacts/002oa09s_WhatsApp%20Image%202026-04-02%20at%2011.42.23%20AM.jpeg',
  },
  {
    icon: PaintBucket,
    title: 'Revestimiento y Acabados',
    desc: 'Acabados arquitectónicos de alta calidad que combinan funcionalidad, durabilidad y estética.',
    backDesc: 'Amplia gama de acabados: aplanados, recubrimientos, pintura, pisos y fachadas. Materiales premium, mano de obra experta.',
    image: 'https://images.unsplash.com/photo-1766961980272-921bba4240bc?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
  },
];

const ServiceCard = ({ service, index }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`card-3d fade-up delay-${(index % 3) + 1}`}
      style={{ height: 340, cursor: 'pointer' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className="card-3d-inner"
        style={{
          width: '100%',
          height: '100%',
          transform: hovered ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front */}
        <div className="card-3d-front" style={{ width: '100%', height: '100%', borderRadius: 12, overflow: 'hidden', position: 'relative', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
          <img src={service.image} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(17,17,17,0.9) 40%, rgba(17,17,17,0.15) 100%)' }} />
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1.5rem' }}>
            <div style={{ width: 42, height: 42, background: 'var(--red)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.75rem' }}>
              <service.icon size={20} color="white" strokeWidth={2} />
            </div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '1rem', color: 'white', marginBottom: '0.4rem' }}>{service.title}</h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{service.desc}</p>
          </div>
        </div>

        {/* Back */}
        <div className="card-3d-back" style={{ width: '100%', height: '100%', borderRadius: 12, background: 'var(--red)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.5rem' }}>
            <service.icon size={26} color="white" strokeWidth={1.5} />
          </div>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.1rem', color: 'white', marginBottom: '1rem' }}>{service.title}</h3>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.7, marginBottom: '1.5rem' }}>{service.backDesc}</p>
          <button
            onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
            style={{ background: 'white', color: 'var(--red)', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.6rem 1.5rem', borderRadius: 4, border: 'none', cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            Solicitar
          </button>
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const ref = useScrollAnimation(0.05);

  return (
    <section id="servicios" ref={ref} style={{ padding: '8rem 2rem', background: 'var(--bg-secondary)', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1200, margin: '0 auto' }}>
        <div className="fade-up" style={{ marginBottom: '4rem' }}>
          <div className="section-label">Lo que hacemos</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontWeight: 900, fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--text-primary)', lineHeight: 1.15 }}>
              Nuestros <span style={{ color: 'var(--red)' }}>Servicios</span>
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.95rem', color: 'var(--gray)', maxWidth: 360, lineHeight: 1.6, textAlign: 'right' }}>
              Pasa el cursor sobre cada tarjeta para conocer más detalles.
            </p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.25rem' }} className="services-grid">
          {services.map((service, i) => <ServiceCard key={i} service={service} index={i} />)}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .services-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 520px) { .services-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
};

export default Services;
